import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
  updatePassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../services/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserPassword: (newPassword: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    let unsubscribe: () => void;

    try {
      // التأكد من وجود كائن auth
      if (!auth) {
        throw new Error('لم يتم تهيئة Firebase Auth بشكل صحيح');
      }

      // الاشتراك في تغييرات حالة المصادقة
      unsubscribe = onAuthStateChanged(
        auth,
        (user) => {
          setUser(user);
          setLoading(false);
          setAuthInitialized(true);
        },
        (error) => {
          console.error('خطأ في مراقبة حالة المصادقة:', error);
          setError('حدث خطأ في نظام المصادقة');
          setLoading(false);
          setAuthInitialized(true);
        }
      );
    } catch (err) {
      console.error('خطأ في تهيئة المصادقة:', err);
      setError('فشل في تهيئة نظام المصادقة');
      setLoading(false);
      setAuthInitialized(true);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!authInitialized) {
      throw new Error('نظام المصادقة غير جاهز');
    }

    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'حدث خطأ أثناء تسجيل الدخول';
      setError(errorMessage);
      throw err;
    }
  };

  const signOut = async () => {
    if (!authInitialized) {
      throw new Error('نظام المصادقة غير جاهز');
    }

    try {
      await firebaseSignOut(auth);
      setUser(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'حدث خطأ أثناء تسجيل الخروج';
      setError(errorMessage);
      throw err;
    }
  };

  const resetPassword = async (email: string) => {
    if (!authInitialized) {
      throw new Error('نظام المصادقة غير جاهز');
    }

    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'حدث خطأ أثناء إعادة تعيين كلمة المرور';
      setError(errorMessage);
      throw err;
    }
  };

  const updateUserPassword = async (newPassword: string) => {
    if (!authInitialized || !user) {
      throw new Error('نظام المصادقة غير جاهز أو المستخدم غير مسجل الدخول');
    }

    try {
      await updatePassword(user, newPassword);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'حدث خطأ أثناء تحديث كلمة المرور';
      setError(errorMessage);
      throw err;
    }
  };

  const clearError = () => setError(null);

  const value = {
    user,
    loading,
    error,
    signIn,
    signOut,
    resetPassword,
    updateUserPassword,
    clearError
  };

  if (!authInitialized) {
    return <div>جاري تهيئة نظام المصادقة...</div>;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth يجب استخدامه داخل AuthProvider');
  }
  return context;
}
